import { useAuth } from "./auth/CognitoAuthContext";
import { useMemo } from "react";

class ApiService {
  #endpoint = null;
  #session = null;

  constructor(endpoint) {
    console.debug("ApiService init");
    this.#endpoint = endpoint;
  }

  updateSession(session) {
    this.#session = session;
  }

  async resourcesGet(options) {
    return await this.#get("resources", options);
  }

  async resourcesDownload(filename, options) {
    return await this.#post("download", { filename }, options);
  }

  async sendAction(payload, options) {
    return await this.#post("action", payload, options);
  }

  async sendMessage(payload, options) {
    return await this.#post("contact", payload, options);
  }

  async checkAuth(payload, options) {
    return await this.#post("authenticated", payload, options);
  }

  async usersGet(payload, options) {
    return await this.#post("manage", payload, options);
  }

  /**
   * Used when wanting to test user loading experience
   * @param delay
   * @returns {Promise<unknown>}
   */
  async fakeAction(delay = 5000) {
    return new Promise((resolve) =>
      setTimeout(() => {
        resolve({ code: 200, success: true });
      }, delay),
    );
  }

  async #post(path, data, options = {}) {
    if (this.#session?.authenticated) {
      try {
        const headers = this.#createHeaders();
        const response = await fetch(`${this.#endpoint}/${path}`, {
          headers,
          method: "POST",
          body: JSON.stringify(data),
          signal: options.signal,
        });

        if (!response.ok) {
          // console.log(`response: ${response.status}`);
          return { code: response.status, success: false };
        }

        return { code: 200, success: response.ok, data: await response.json() };
      } catch (e) {
        if (e.name === "AbortError") {
          return { code: 500, success: false };
        }
        console.error(`request failed: ${path}`, e);
        return { code: 500, success: false };
      }
    } else {
      console.debug("User not authenticated");
      return {};
    }
  }

  async #get(path, options = {}) {
    if (this.#session?.authenticated) {
      try {
        const headers = this.#createHeaders();
        const response = await fetch(`${this.#endpoint}/${path}`, {
          headers,
          method: "GET",
          signal: options.signal,
        });

        if (!response.ok) {
          // console.log(`response: ${response.status}`);
          return { code: response.status, success: false };
        }

        return { code: 200, success: response.ok, data: await response.json() };
      } catch (e) {
        if (e.name === "AbortError") {
          return { code: 500, success: false };
        }
        console.error(`request failed: ${path}`, e);
        return { code: 500, success: false };
      }
    } else {
      console.debug("User not authenticated");
      return {};
    }
  }

  #createHeaders() {
    return {
      "Content-Type": "application/json",
      "Authorization": `Bearer ${this.#session?.token || "missing"}`,
    };
  }

  // used to ensure react only creates one auth service
  static #instance;

  static getInstance(endpoint, session) {
    if (!this.#instance) {
      this.#instance = new ApiService(endpoint);
    }
    // ensure session matches
    this.#instance.updateSession(session);
    return this.#instance;
  }
}

export const useApi = () => {
  const { session } = useAuth();

  // Use environment variables for Cognito configuration
  const { VITE_API_ENDPOINT } = import.meta.env;

  return useMemo(() => ApiService.getInstance(VITE_API_ENDPOINT, session), [VITE_API_ENDPOINT, session]);
};
